/**
 * Which settlement document the HUD balance is built from
 * (funding-statements.md § "Which HUD the balance uses", 2026-10-05).
 *
 * `GET /funding_statements/{dealId}/net_wire` carries `hudSource` —
 * {documentId, fileStorageName, fileName, docType, docVariant, altaVariant,
 * isAlta, isExecuted, settlementDate, parsed, canBalanceBorrowerSide, selection}
 * — and `hudSourceWarnings[]`. A loan can hold several settlement statements (a
 * draft, the signed HUD-1 uploaded twice…); before this the calculator picked
 * one silently and the closer could not tell which one the variance was against.
 *
 * Renders nothing when the backend sends neither field, so it is safe to ship
 * before the route that adds them is deployed.
 */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import { Pill, T, labelSx } from "../wireFundingShared";

// ALTA templates as the extractor names them → the label the doc asks for.
const ALTA_LABEL = {
  "Borrower-Buyer": "Borrower/Buyer",
  Seller: "Seller",
  Combined: "Combined",
  Cash: "Cash",
};

/** "HUD-1", "ALTA: Seller", "Closing Disclosure", "Preliminary HUD", … */
export const settlementTypeLabel = (src) => {
  if (!src) return null;
  if (src.isAlta || src.docVariant === "ALTA Settlement") {
    const v = ALTA_LABEL[src.altaVariant] || src.altaVariant;
    return v ? `ALTA: ${v}` : "ALTA";
  }
  return src.docVariant || src.docType || null;
};

const SELECTION_LABEL = {
  selected: "Selected",
  workbook: "From the settlement workbook",
  auto: "Automatic",
};

/**
 * Resolve a settlement document to the full loan-document object the previewer
 * needs — by `documentId` (the loan-document oid) first, then by storage name.
 */
export const useResolveLoanDocument = () => {
  const loanDocuments = useSelector((s) => s.loanDrilldown?.loanDocuments);
  return useMemo(() => {
    const byId = new Map();
    const byFsn = new Map();
    (loanDocuments || []).forEach((d) => {
      if (d?._id) byId.set(String(d._id), d);
      // Loan documents carry snake_case `file_storage_name`.
      const fsn = d?.file_storage_name || d?.fileStorageName;
      if (fsn) byFsn.set(fsn, d);
    });
    return (ref) =>
      (ref?.documentId && byId.get(String(ref.documentId))) ||
      (ref?.fileStorageName && byFsn.get(ref.fileStorageName)) ||
      null;
  }, [loanDocuments]);
};

const linkSx = {
  fontSize: 12.5,
  fontWeight: 600,
  color: T.accentInk,
  borderRadius: "4px",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: `2px solid ${T.accent}`, outlineOffset: 2 },
  "&.Mui-disabled": { color: T.faint },
};

const HudSourceChip = ({
  hudSource,
  warnings,
  actions,
  children,
  label = "Balanced against",
}) => {
  const dispatch = useDispatch();
  const resolveDoc = useResolveLoanDocument();

  const list = Array.isArray(warnings) ? warnings.filter(Boolean) : [];
  if (!hudSource && !list.length) return null;

  const doc = resolveDoc(hudSource);
  const openPreview = () => {
    if (doc) dispatch(filePreviewSetData(doc));
  };
  const type = settlementTypeLabel(hudSource);
  const selection = SELECTION_LABEL[hudSource?.selection];
  const name = hudSource?.fileName || hudSource?.fileStorageName;

  return (
    <Flx column gap={1} sx={{ mt: 1.5 }}>
      {hudSource ? (
        <Flx
          ac
          jb
          wrap
          gap={1.5}
          sx={{
            px: 1.5,
            py: 1.1,
            border: `1px solid ${T.line}`,
            borderRadius: T.radiusSm,
            bgcolor: T.subtle,
          }}
        >
          <Flx ac gap={1.25} sx={{ minWidth: 0 }}>
            <DescriptionOutlined sx={{ fontSize: 18, color: T.muted, flex: "none" }} />
            <Flx column gap={0.4} sx={{ minWidth: 0 }}>
              <Txt sx={{ ...labelSx, display: "block" }}>{label}</Txt>
              <Flx ac wrap gap={0.75} sx={{ minWidth: 0 }}>
                <ButtonBase
                  onClick={openPreview}
                  disabled={!doc}
                  title={doc ? "Preview this document" : "Document not found on the loan"}
                  sx={{
                    ...linkSx,
                    color: doc ? T.ink : T.ink2,
                    fontSize: 13,
                    textAlign: "left",
                    wordBreak: "break-word",
                  }}
                >
                  {name || "Settlement statement"}
                </ButtonBase>
                {type ? <Pill label={type} tone="info" /> : null}
                {hudSource.isExecuted === true ? (
                  <Pill label="Executed" tone="success" />
                ) : hudSource.isExecuted === false ? (
                  <Pill label="Draft" tone="warning" />
                ) : null}
                {selection ? <Pill label={selection} outlined /> : null}
              </Flx>
            </Flx>
          </Flx>
          <Flx ac gap={1.5} sx={{ flex: "none" }}>
            <ButtonBase onClick={openPreview} disabled={!doc} sx={linkSx}>
              View
            </ButtonBase>
            {actions}
          </Flx>
        </Flx>
      ) : null}

      {list.length ? (
        <Flx column gap={0.5}>
          {list.map((w, i) => (
            <Flx key={`${i}-${w}`} gap={0.75} sx={{ alignItems: "flex-start" }}>
              <WarningAmberRounded
                sx={{ fontSize: 15, color: T.amber, mt: "1px", flex: "none" }}
              />
              <Txt sx={{ fontSize: 12, color: T.amber, lineHeight: 1.45 }}>{w}</Txt>
            </Flx>
          ))}
        </Flx>
      ) : null}

      {children ? <Box>{children}</Box> : null}
    </Flx>
  );
};

HudSourceChip.propTypes = {
  hudSource: PropTypes.object,
  warnings: PropTypes.arrayOf(PropTypes.string),
  /** Extra controls at the chip's end (the Change button, item 90). */
  actions: PropTypes.node,
  /** Rendered under the chip (the newer-version banner, item 91). */
  children: PropTypes.node,
  /** Caption above the file name (the settlement workbook says "Built from"). */
  label: PropTypes.string,
};

export default HudSourceChip;
